import React, { useState, useEffect } from 'react';
import { Shield, CheckCircle, FileCheck, Search, Award, Star, ArrowRight, Clock, TrendingUp, Lock } from 'lucide-react';
import { serviceTestimonials, serviceProcessSteps, images } from '../../mock';
import PricingSection from '../../components/PricingSection';
import InnerPageHero from '../../components/InnerPageHero';
import InnerPageBanner from '../../components/InnerPageBanner';
import { Button } from '../../components/ui/button';

const RegisterTrademark = ({ onOpenPopup }) => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const testimonials = serviceTestimonials.trademark;
  const processSteps = serviceProcessSteps.trademark;

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <InnerPageHero
        badge="TRADEMARK REGISTRATION"
        title="Register Your Trademark"
        description="Protect your brand with professional trademark registration services. Fast, reliable, and affordable protection for your business identity with 95% approval rate."
      />

      {/* Key Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Register Your Trademark?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              A registered trademark provides comprehensive legal protection and exclusive rights for your brand identity, setting the foundation for long-term business success.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-10">
            <div className="group bg-gradient-to-br from-white to-[#E2F8FE] p-10 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100">
              <div className="bg-gradient-to-br from-[#FF4A2A] to-[#E63E1F] w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                <Shield className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">
                Legal Protection
              </h3>
              <p className="text-base text-gray-600 text-center leading-relaxed">
                Gain exclusive nationwide rights to use your trademark and legal recourse against infringement. Federal registration provides the strongest protection available.
              </p>
            </div>

            <div className="group bg-gradient-to-br from-white to-[#E2F8FE] p-10 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100">
              <div className="bg-gradient-to-br from-[#FF4A2A] to-[#E63E1F] w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                <Award className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">
                Brand Recognition
              </h3>
              <p className="text-base text-gray-600 text-center leading-relaxed">
                Build consumer trust and establish your brand's credibility in the marketplace. The ® symbol shows customers your brand is legitimate and protected.
              </p>
            </div>

            <div className="group bg-gradient-to-br from-white to-[#E2F8FE] p-10 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100">
              <div className="bg-gradient-to-br from-[#FF4A2A] to-[#E63E1F] w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                <TrendingUp className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">
                Business Asset
              </h3>
              <p className="text-base text-gray-600 text-center leading-relaxed">
                Increase your company's value with registered intellectual property assets that can be licensed, sold, or used as collateral for financing.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Registration Process - 3rd Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block bg-[#E2F8FE] rounded-full px-6 py-2 mb-4">
              <span className="text-[#FF4A2A] font-semibold text-base">HOW IT WORKS</span>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Registration Process
            </h2>
            <p className="text-lg text-gray-600 max-w-xl mx-auto">
              Simple, transparent steps to protect your brand
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="bg-gradient-to-br from-white to-[#E2F8FE] rounded-xl shadow-lg p-8 border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-center gap-4 mb-6">
                  <span className="bg-gradient-to-br from-[#FF4A2A] to-[#E63E1F] text-white text-lg font-bold px-6 py-3 rounded-lg shadow-md">
                    {step.step}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {step.title}
                </h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What's Included in Our Service
            </h2>
            <p className="text-lg text-gray-600">
              Comprehensive trademark registration with expert support
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="flex items-start gap-4 bg-white p-6 rounded-xl shadow-md">
              <CheckCircle className="h-6 w-6 text-[#FF4A2A] flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Comprehensive Trademark Search</h3>
                <p className="text-base text-gray-600">We search federal, state, and common law databases to identify conflicts</p>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-white p-6 rounded-xl shadow-md">
              <CheckCircle className="h-6 w-6 text-[#FF4A2A] flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Expert Application Preparation</h3>
                <p className="text-base text-gray-600">Professional preparation with accurate classifications and descriptions</p>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-white p-6 rounded-xl shadow-md">
              <CheckCircle className="h-6 w-6 text-[#FF4A2A] flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-gray-900 mb-2">USPTO Filing & Monitoring</h3>
                <p className="text-base text-gray-600">We file with the USPTO and monitor your application's progress</p>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-white p-6 rounded-xl shadow-md">
              <CheckCircle className="h-6 w-6 text-[#FF4A2A] flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Office Action Response</h3>
                <p className="text-base text-gray-600">Expert handling of any USPTO office actions or objections</p>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-white p-6 rounded-xl shadow-md">
              <CheckCircle className="h-6 w-6 text-[#FF4A2A] flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Dedicated Case Manager</h3>
                <p className="text-base text-gray-600">Personal support throughout the entire registration process</p>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-white p-6 rounded-xl shadow-md">
              <CheckCircle className="h-6 w-6 text-[#FF4A2A] flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Trademark Monitoring</h3>
                <p className="text-base text-gray-600">Ongoing monitoring for potential infringement after registration</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Creative Banner */}
      <InnerPageBanner onOpenPopup={onOpenPopup} />

      {/* Pricing Section */}
      <PricingSection onOpenPopup={onOpenPopup} />

      {/* Testimonials - Sliding Display */}
      <section className="py-20 bg-gradient-to-br from-[#E2F8FE] via-white to-[#E2F8FE]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block bg-white rounded-full px-6 py-2 mb-4 shadow-md">
              <span className="text-[#FF4A2A] font-semibold text-base">TESTIMONIALS</span>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-lg text-gray-600">
              Trusted by businesses for trademark registration
            </p>
          </div>

          {/* Desktop: Show 3 testimonials with slider */}
          <div className="hidden md:block relative">
            <div className="grid grid-cols-3 gap-8">
              {testimonials.slice(currentTestimonial, currentTestimonial + 3).map((testimonial, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-8 border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                  <div className="flex justify-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 text-[#FF4A2A] fill-[#FF4A2A]" />
                    ))}
                  </div>
                  <p className="text-base text-gray-700 italic leading-relaxed mb-6">
                    "{testimonial.text}"
                  </p>
                  <div className="pt-4 border-t border-gray-200">
                    <p className="font-bold text-gray-900 text-base">
                      {testimonial.name}
                    </p>
                    <p className="text-gray-600 text-sm mt-1">
                      {testimonial.company}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-center gap-2 mt-12">
              {Array.from({ length: Math.ceil(testimonials.length / 3) }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index * 3)}
                  className={`h-3 rounded-full transition-all duration-300 ${
                    Math.floor(currentTestimonial / 3) === index ? 'w-12 bg-[#FF4A2A]' : 'w-3 bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Mobile: Show slider */}
          <div className="md:hidden relative bg-white rounded-xl shadow-lg p-8 border border-gray-100">
            <div className="text-center space-y-6">
              <div className="flex justify-center mb-4">
                {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                  <Star key={i} className="h-6 w-6 text-[#FF4A2A] fill-[#FF4A2A]" />
                ))}
              </div>
              <p className="text-base text-gray-700 italic leading-relaxed">
                "{testimonials[currentTestimonial].text}"
              </p>
              <div className="pt-6 border-t border-gray-200">
                <p className="font-bold text-gray-900 text-base">
                  {testimonials[currentTestimonial].name}
                </p>
                <p className="text-gray-600 text-sm mt-1">
                  {testimonials[currentTestimonial].company}
                </p>
              </div>
            </div>
            <div className="flex justify-center gap-2 mt-8">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`h-3 rounded-full transition-all duration-300 ${
                    index === currentTestimonial ? 'w-12 bg-[#FF4A2A]' : 'w-3 bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-4">
            <div className="bg-gradient-to-r from-[#E2F8FE] to-white rounded-xl shadow-md p-6 border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                How long does trademark registration take?
              </h3>
              <p className="text-base text-gray-600 leading-relaxed">
                The entire process typically takes 8-12 months from filing to registration, depending on USPTO workload and any office actions that may arise.
              </p>
            </div>

            <div className="bg-gradient-to-r from-[#E2F8FE] to-white rounded-xl shadow-md p-6 border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                What can be trademarked?
              </h3>
              <p className="text-base text-gray-600 leading-relaxed">
                You can trademark brand names, logos, slogans, product names, and even sounds or colors that distinguish your goods or services from competitors.
              </p>
            </div>

            <div className="bg-gradient-to-r from-[#E2F8FE] to-white rounded-xl shadow-md p-6 border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                What's included in the trademark search?
              </h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Our comprehensive search checks federal databases, state registrations, and common law uses to identify potential conflicts before filing your application.
              </p>
            </div>

            <div className="bg-gradient-to-r from-[#E2F8FE] to-white rounded-xl shadow-md p-6 border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                What if my trademark application is rejected?
              </h3>
              <p className="text-base text-gray-600 leading-relaxed">
                We handle all office actions and work with you to address any USPTO concerns. Our high approval rate comes from thorough pre-filing research and expert preparation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#FF4A2A] to-[#E63E1F]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Register Your Trademark?
          </h2>
          <p className="text-lg text-white opacity-90 mb-8">
            Protect your brand today with our professional trademark registration services
          </p>
          <Button
            onClick={onOpenPopup}
            className="bg-white text-[#FF4A2A] hover:bg-gray-100 px-10 py-6 text-lg font-semibold rounded-xl shadow-xl"
          >
            Get Started Now <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default RegisterTrademark;